import { useCallback, useEffect, useMemo, useState } from 'react';
import { checkStructure, type StructureIssue } from '@peruselab/api-client/structureChecks';
import { moleculeSourceKey, toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import type { DrawingDocument } from '../generated/drawing-contract';

/** The marks the server's answer puts on the paper (ADR 0020 D3). */
export interface StructureCheckMarks {
  readonly issues: readonly StructureIssue[];
  readonly errorAtomIds: ReadonlySet<string>;
  readonly errorBondIds: ReadonlySet<string>;
  readonly warningAtomIds: ReadonlySet<string>;
  readonly warningBondIds: ReadonlySet<string>;
}

type Answer =
  | { readonly sourceKey: string; readonly issues: readonly StructureIssue[] }
  | { readonly sourceKey: string; readonly failed: true };

// The findings that judge an atom by the bonds the server sees.
const BOND_TYPING_RULES = new Set(['unusual-bonding', 'unusual-charge']);

/**
 * ADR 0026 D6 (#1157, owner test): an agostic interaction is drawn, never part of the molecule the
 * server checks -- so a metal held only by one looks unbonded there ("an uncharged Ti with 0
 * bonds"). Those findings, about such metals alone, are left out; the paper says what holds them.
 */
export function withoutAgosticMetals(
  document: DrawingDocument,
  issues: readonly StructureIssue[]
): readonly StructureIssue[] {
  const metals = new Set(
    document.structure.graph.edges.flatMap((edge) =>
      document.structure.bonds[edge.id]?.type === 'agostic' ? [edge.target] : []
    )
  );
  if (metals.size === 0) return issues;
  return issues.filter(
    (issue) =>
      !BOND_TYPING_RULES.has(issue.rule) || issue.atomIds.length === 0 || !issue.atomIds.every((id) => metals.has(id))
  );
}

/** The atom and bond ids of the issues of one severity; a warning confirmed intended is unmarked (D5). */
export function marksOf(issues: readonly StructureIssue[]): StructureCheckMarks {
  const ids = (severity: StructureIssue['severity'], key: 'atomIds' | 'bondIds') =>
    new Set(
      issues.filter((issue) => issue.severity === severity && !issue.confirmation).flatMap((issue) => issue[key])
    );
  return {
    issues,
    errorAtomIds: ids('ERROR', 'atomIds'),
    errorBondIds: ids('ERROR', 'bondIds'),
    warningAtomIds: ids('WARNING', 'atomIds'),
    warningBondIds: ids('WARNING', 'bondIds')
  };
}

/**
 * ADR 0020 D1 (#1099): the server's structure check of the whole paper, after each settled edit
 * (the same 300 ms as the browser's check). The paper is checked as one molecule document, so a
 * salt's counter-ion beside it counts. Signed in only (`enabled`).
 *
 * #1101: `paused` while a fix is previewed on the paper -- nothing is asked and the last answer
 * stays, so the entry being hovered doesn't vanish; `recheck` asks again (after a confirmation).
 *
 * @returns `marks` for the drawing as it is now, or null while the answer is pending, when it
 *     failed (the browser's own check then stands), or when the check is off; `failed` once it
 *     couldn't be had for this drawing.
 */
export function useStructureCheck(
  document: DrawingDocument,
  enabled: boolean,
  paused = false
): { readonly marks: StructureCheckMarks | null; readonly failed: boolean; readonly recheck: () => void } {
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [asked, setAsked] = useState(0);
  const sourceKey = moleculeSourceKey(document);
  const empty = document.structure.graph.nodes.length === 0;
  const recheck = useCallback(() => setAsked((count) => count + 1), []);

  useEffect(() => {
    if (!enabled || empty || paused) return;
    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => {
      checkStructure(toChemMoleculeV1(document), controller.signal)
        .then((result) => setAnswer({ sourceKey, issues: withoutAgosticMetals(document, result.issues) }))
        .catch(() => {
          if (!controller.signal.aborted) setAnswer({ sourceKey, failed: true });
        });
    }, 300);
    return () => {
      globalThis.clearTimeout(timeout);
      controller.abort();
    };
    // The document is read through its source key: only a change to its chemistry asks again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, empty, sourceKey, paused, asked]);

  const current = enabled && !empty && (paused || answer?.sourceKey === sourceKey) ? answer : null;
  const marks = useMemo(() => (current && 'issues' in current ? marksOf(current.issues) : null), [current]);
  return { marks, failed: current !== null && 'failed' in current, recheck };
}
